<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import { toggleScoring } from '../helpers/scoring';

const { t } = useI18n();

const fields = defineModel({ type: Array, required: true });

// Segundo ponto de entrada da pontuação (o primeiro é o toggle da aba
// Destino, em FormScoringEditor.vue). Os dois lêem/escrevem o MESMO
// `form.scoring` — ligar por aqui ou por lá dá no mesmo estado. Ver
// docs/superpowers/specs/2026-08-10-forms-pontuacao-lead-design.md.
const scoring = defineModel('scoring', { type: Object, default: () => ({}) });

const scoringEnabled = computed(() => !!scoring.value?.enabled);

const onToggleScoring = enabled => {
  scoring.value = toggleScoring(scoring.value, enabled);
};

const FIELD_TYPES = [
  'text',
  'textarea',
  'email',
  'phone',
  'number',
  'date',
  'select',
  'radio',
  'checkbox',
  'consent',
  'file',
];

const OPTION_TYPES = ['select', 'radio', 'checkbox'];

// F3.5 — lógica condicional: operadores da regra visible_if.
const CONDITION_OPERATORS = ['equals', 'not_equals', 'includes', 'filled'];

// Campos que podem ser a FONTE de uma condição (qualquer outro campo já
// nomeado; normalmente um campo anterior).
const conditionSources = index =>
  fields.value.filter((field, i) => i !== index && field.key && field.label);

const operatorLabel = op =>
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  t(`FORMS_MGMT.EDITOR.FIELDS.OPERATORS.${op.toUpperCase()}`);

// Estilo efetivo do campo de escolha (espelha o FormField do form público):
// default select=lista, radio/checkbox=botão; choice_style manda quando setado.
const fieldChoiceStyle = field =>
  field.choice_style === 'list' ? 'list' : 'button';

const slugify = value =>
  value
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const uniqueKey = base => {
  const usedKeys = fields.value.map(field => field.key);
  let candidate = base || 'campo';
  let index = 1;
  while (usedKeys.includes(candidate)) {
    index += 1;
    candidate = `${base || 'campo'}_${index}`;
  }
  return candidate;
};

const addField = () => {
  const index = fields.value.length + 1;
  fields.value = [
    ...fields.value,
    {
      key: uniqueKey(`campo_${index}`),
      type: 'text',
      label: '',
      required: false,
      placeholder: '',
      options: [],
      // Steps (passo a passo) layout only: when true, this field shares the
      // same screen as the field immediately above it instead of getting
      // its own step. Meaningless for the very first field (there is no
      // "above" for it to join) — see useFormSteps.js buildSteps().
      group_with_previous: false,
    },
  ];
};

const removeField = index => {
  fields.value = fields.value.filter((_, i) => i !== index);
};

const moveField = (index, direction) => {
  const targetIndex = index + direction;
  if (targetIndex < 0 || targetIndex >= fields.value.length) return;
  const updated = [...fields.value];
  const [moved] = updated.splice(index, 1);
  updated.splice(targetIndex, 0, moved);
  fields.value = updated;
};

const updateField = (index, key, value) => {
  const updated = [...fields.value];
  const field = { ...updated[index], [key]: value };
  if (key === 'label' && (!field.key || field.key === '')) {
    field.key = uniqueKey(slugify(value));
  }
  updated[index] = field;
  fields.value = updated;
};

const toggleCondition = (index, enabled) => {
  updateField(
    index,
    'visible_if',
    enabled ? { field: '', operator: 'equals', value: '' } : null
  );
};

const updateCondition = (index, patch) => {
  const current = fields.value[index].visible_if || {};
  updateField(index, 'visible_if', { ...current, ...patch });
};

const addOption = index => {
  const updated = [...fields.value];
  const field = { ...updated[index] };
  field.options = [...(field.options || []), ''];
  updated[index] = field;
  fields.value = updated;
};

const optionPointsFor = field => field.option_points || {};

const updateOption = (index, optionIndex, value) => {
  const updated = [...fields.value];
  const field = { ...updated[index] };
  const options = [...(field.options || [])];
  const previous = options[optionIndex];
  options[optionIndex] = value;
  field.options = options;

  // A pontuação é indexada pelo TEXTO da opção: renomear precisa mover a
  // entrada, ou o peso configurado vira órfão silenciosamente. Mas se o
  // novo texto já pertence a OUTRA opção (usuário criou duplicata) ou ficou
  // vazio, não dá pra migrar com segurança — mover sobrescreveria/fundiria
  // a pontuação da outra opção, ou criaria uma entrada fantasma sob ''. Ou
  // seja: nunca perder o peso de uma opção diferente; se preciso, perde-se
  // só o peso da própria opção renomeada, descartando a entrada antiga.
  const table = { ...optionPointsFor(field) };
  if (previous !== value && Object.hasOwn(table, previous)) {
    const collides = value !== '' && Object.hasOwn(table, value);
    if (!collides && value !== '') {
      table[value] = table[previous];
    }
    delete table[previous];
    field.option_points = table;
  }

  updated[index] = field;
  fields.value = updated;
};

const removeOption = (index, optionIndex) => {
  const updated = [...fields.value];
  const field = { ...updated[index] };
  const removed = (field.options || [])[optionIndex];
  field.options = (field.options || []).filter((_, i) => i !== optionIndex);
  const table = { ...optionPointsFor(field) };
  delete table[removed];
  field.option_points = table;
  updated[index] = field;
  fields.value = updated;
};

const updateOptionPoints = (index, optionValue, points) => {
  const updated = [...fields.value];
  const field = { ...updated[index] };
  const table = { ...optionPointsFor(field) };
  const parsed = Number(points);
  if (!points && points !== 0) delete table[optionValue];
  else table[optionValue] = Number.isFinite(parsed) ? parsed : 0;
  field.option_points = table;
  updated[index] = field;
  fields.value = updated;
};

// Campo sem opção (texto, textarea, email, telefone, número, data) pontua
// por ter sido respondido, com um único número — espelha
// Forms::ScoringService#answer_field_max. Mesma regra de "esvaziou, some a
// chave" do updateOptionPoints acima, só que sem tabela por opção.
const updateAnswerPoints = (index, points) => {
  const updated = [...fields.value];
  const field = { ...updated[index] };
  const parsed = Number(points);
  if (!points && points !== 0) delete field.answer_points;
  else field.answer_points = Number.isFinite(parsed) ? parsed : 0;
  updated[index] = field;
  fields.value = updated;
};

defineExpose({
  updateOption,
  updateOptionPoints,
  removeOption,
  updateAnswerPoints,
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-center justify-between">
      <div class="flex flex-col gap-1">
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ t('FORMS_MGMT.EDITOR.FIELDS.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ t('FORMS_MGMT.EDITOR.FIELDS.DESCRIPTION') }}
        </p>
      </div>
      <Button
        icon="i-lucide-plus"
        faded
        slate
        :label="t('FORMS_MGMT.EDITOR.FIELDS.ADD')"
        @click="addField"
      />
    </div>

    <!-- Segundo ponto de entrada da pontuação — ver comentário no script.
         O toggle da aba Destino (FormScoringEditor.vue) continua existindo;
         este aqui só torna a pontuação descobrível de onde os pontos por
         opção realmente aparecem. -->
    <div
      class="flex flex-col gap-2 border border-n-weak rounded-md p-4 bg-n-alpha-1"
    >
      <label class="flex items-center gap-2 cursor-pointer">
        <input
          data-testid="scoring-toggle"
          type="checkbox"
          class="w-4 h-4"
          :checked="scoringEnabled"
          @change="onToggleScoring($event.target.checked)"
        />
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('FORMS_MGMT.EDITOR.FIELDS.SCORING.TOGGLE') }}
        </span>
      </label>
      <p v-if="scoringEnabled" class="text-xs text-n-slate-11">
        {{ t('FORMS_MGMT.EDITOR.FIELDS.SCORING.HELP_ON') }}
      </p>
    </div>

    <div
      v-if="!fields.length"
      class="text-sm text-n-slate-11 border border-dashed border-n-weak rounded-md p-6 text-center"
    >
      {{ t('FORMS_MGMT.EDITOR.FIELDS.EMPTY') }}
    </div>

    <Draggable
      v-model="fields"
      item-key="key"
      handle=".csf-field-handle"
      :animation="160"
      ghost-class="opacity-40"
      class="flex flex-col gap-4"
    >
      <template #item="{ element: field, index }">
        <div
          class="flex flex-col gap-3 border border-n-weak rounded-md p-4 bg-n-alpha-1"
        >
          <div class="flex items-start justify-between gap-2">
            <span
              class="csf-field-handle mt-1 shrink-0 flex flex-col items-center gap-0.5 cursor-grab text-n-slate-10 hover:text-n-slate-12"
              :title="t('FORMS_MGMT.EDITOR.FIELDS.DRAG')"
            >
              <span class="i-lucide-grip-vertical size-4" />
              <span class="text-xs font-medium text-n-slate-11">
                {{ index + 1 }}
              </span>
            </span>
            <div class="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input
                :model-value="field.label"
                :label="t('FORMS_MGMT.EDITOR.FIELDS.LABEL')"
                :placeholder="t('FORMS_MGMT.EDITOR.FIELDS.LABEL_PLACEHOLDER')"
                @update:model-value="
                  value => updateField(index, 'label', value)
                "
              />
              <label class="flex flex-col gap-1">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ t('FORMS_MGMT.EDITOR.FIELDS.TYPE') }}
                </span>
                <select
                  :value="field.type"
                  class="form-select"
                  @change="
                    event => updateField(index, 'type', event.target.value)
                  "
                >
                  <option v-for="type in FIELD_TYPES" :key="type" :value="type">
                    <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
                    {{ t(`FORMS_MGMT.EDITOR.FIELDS.TYPES.${type}`) }}
                  </option>
                </select>
              </label>
              <Input
                :model-value="field.placeholder"
                :label="t('FORMS_MGMT.EDITOR.FIELDS.PLACEHOLDER')"
                :placeholder="
                  t('FORMS_MGMT.EDITOR.FIELDS.PLACEHOLDER_PLACEHOLDER')
                "
                @update:model-value="
                  value => updateField(index, 'placeholder', value)
                "
              />
              <label class="flex items-center gap-2 mt-6">
                <input
                  :checked="field.required"
                  type="checkbox"
                  class="w-4 h-4"
                  @change="
                    event =>
                      updateField(index, 'required', event.target.checked)
                  "
                />
                <span class="text-sm font-medium text-n-slate-12">
                  {{ t('FORMS_MGMT.EDITOR.FIELDS.REQUIRED') }}
                </span>
              </label>
              <label
                class="flex items-center gap-2 mt-1"
                :class="{ 'opacity-50': index === 0 }"
              >
                <input
                  :checked="!!field.group_with_previous"
                  :disabled="index === 0"
                  type="checkbox"
                  class="w-4 h-4"
                  @change="
                    event =>
                      updateField(
                        index,
                        'group_with_previous',
                        event.target.checked
                      )
                  "
                />
                <span class="text-sm font-medium text-n-slate-12">
                  {{ t('FORMS_MGMT.EDITOR.FIELDS.GROUP_WITH_PREVIOUS') }}
                </span>
              </label>
            </div>
            <div class="flex flex-col gap-1 shrink-0">
              <Button
                v-tooltip.top="t('FORMS_MGMT.EDITOR.FIELDS.MOVE_UP')"
                icon="i-lucide-chevron-up"
                slate
                xs
                faded
                :disabled="index === 0"
                @click="moveField(index, -1)"
              />
              <Button
                v-tooltip.top="t('FORMS_MGMT.EDITOR.FIELDS.MOVE_DOWN')"
                icon="i-lucide-chevron-down"
                slate
                xs
                faded
                :disabled="index === fields.length - 1"
                @click="moveField(index, 1)"
              />
              <Button
                v-tooltip.top="t('FORMS_MGMT.EDITOR.FIELDS.REMOVE')"
                icon="i-lucide-trash-2"
                ruby
                xs
                faded
                @click="removeField(index)"
              />
            </div>
          </div>

          <!-- F3.5 — condição de exibição (mostrar o campo só se outro campo
               bater a regra). Só faz sentido quando há outro campo de fonte. -->
          <div
            v-if="conditionSources(index).length"
            class="flex flex-col gap-2 pl-8"
          >
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                class="w-4 h-4"
                :checked="!!field.visible_if"
                @change="event => toggleCondition(index, event.target.checked)"
              />
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('FORMS_MGMT.EDITOR.FIELDS.CONDITION') }}
              </span>
            </label>
            <div
              v-if="field.visible_if"
              class="grid grid-cols-1 md:grid-cols-3 gap-2"
            >
              <select
                :value="field.visible_if.field"
                class="form-select"
                @change="
                  event => updateCondition(index, { field: event.target.value })
                "
              >
                <option value="">
                  {{ t('FORMS_MGMT.EDITOR.FIELDS.CONDITION_FIELD') }}
                </option>
                <option
                  v-for="src in conditionSources(index)"
                  :key="src.key"
                  :value="src.key"
                >
                  {{ src.label }}
                </option>
              </select>
              <select
                :value="field.visible_if.operator"
                class="form-select"
                @change="
                  event =>
                    updateCondition(index, { operator: event.target.value })
                "
              >
                <option v-for="op in CONDITION_OPERATORS" :key="op" :value="op">
                  {{ operatorLabel(op) }}
                </option>
              </select>
              <input
                v-if="field.visible_if.operator !== 'filled'"
                :value="field.visible_if.value"
                type="text"
                class="form-input"
                :placeholder="t('FORMS_MGMT.EDITOR.FIELDS.CONDITION_VALUE')"
                @input="
                  event => updateCondition(index, { value: event.target.value })
                "
              />
            </div>
          </div>

          <div
            v-if="OPTION_TYPES.includes(field.type)"
            class="flex flex-col gap-2 pl-8"
          >
            <div class="flex items-center gap-2">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('FORMS_MGMT.EDITOR.FIELDS.CHOICE_STYLE') }}
              </span>
              <div class="flex gap-1.5">
                <Button
                  size="xs"
                  icon="i-lucide-square-mouse-pointer"
                  :faded="fieldChoiceStyle(field) !== 'button'"
                  :slate="fieldChoiceStyle(field) !== 'button'"
                  :blue="fieldChoiceStyle(field) === 'button'"
                  :label="t('FORMS_MGMT.EDITOR.FIELDS.CHOICE_BUTTON')"
                  @click="updateField(index, 'choice_style', 'button')"
                />
                <Button
                  size="xs"
                  icon="i-lucide-list"
                  :faded="fieldChoiceStyle(field) !== 'list'"
                  :slate="fieldChoiceStyle(field) !== 'list'"
                  :blue="fieldChoiceStyle(field) === 'list'"
                  :label="t('FORMS_MGMT.EDITOR.FIELDS.CHOICE_LIST')"
                  @click="updateField(index, 'choice_style', 'list')"
                />
              </div>
            </div>
            <div class="flex items-center gap-2">
              <span class="text-sm font-medium text-n-slate-12 flex-1 min-w-0">
                {{ t('FORMS_MGMT.EDITOR.FIELDS.OPTIONS') }}
              </span>
              <span
                v-if="scoringEnabled && (field.options || []).length"
                class="w-20 shrink-0 text-xs font-medium text-n-slate-11 text-center"
              >
                {{ t('FORMS_MGMT.EDITOR.FIELDS.OPTION_POINTS') }}
              </span>
              <span class="w-6 shrink-0" />
            </div>
            <p
              v-if="!(field.options || []).length"
              class="text-xs text-n-slate-11"
            >
              {{ t('FORMS_MGMT.EDITOR.FIELDS.OPTIONS_EMPTY') }}
            </p>
            <div
              v-for="(option, optionIndex) in field.options || []"
              :key="optionIndex"
              class="flex items-center gap-2"
            >
              <input
                :value="option"
                type="text"
                class="form-input flex-1 min-w-0"
                :placeholder="t('FORMS_MGMT.EDITOR.FIELDS.OPTIONS_PLACEHOLDER')"
                @input="
                  event => updateOption(index, optionIndex, event.target.value)
                "
              />
              <div v-if="scoringEnabled" class="w-20 shrink-0">
                <input
                  data-testid="option-points"
                  :value="optionPointsFor(field)[option] ?? ''"
                  type="number"
                  min="0"
                  class="form-input w-20"
                  :aria-label="t('FORMS_MGMT.EDITOR.FIELDS.OPTION_POINTS')"
                  @input="
                    event =>
                      updateOptionPoints(index, option, event.target.value)
                  "
                />
              </div>
              <Button
                icon="i-lucide-x"
                ruby
                xs
                faded
                @click="removeOption(index, optionIndex)"
              />
            </div>
            <Button
              icon="i-lucide-plus"
              slate
              xs
              faded
              class="w-fit"
              :label="t('FORMS_MGMT.EDITOR.FIELDS.OPTIONS_ADD')"
              @click="addOption(index)"
            />
          </div>

          <!-- Campo sem opção (texto, textarea, email, telefone, número,
               data): pontua por ter sido respondido, com um único número.
               consent nunca pontua — ver Forms::ScoringService. -->
          <div
            v-if="
              scoringEnabled &&
              field.type !== 'consent' &&
              !OPTION_TYPES.includes(field.type)
            "
            class="flex items-center gap-2 pl-8"
          >
            <label
              :for="`answer-points-${index}`"
              class="text-sm font-medium text-n-slate-12"
            >
              {{ t('FORMS_MGMT.EDITOR.FIELDS.SCORING.ANSWER_POINTS') }}
            </label>
            <div class="w-20 shrink-0">
              <input
                :id="`answer-points-${index}`"
                data-testid="answer-points"
                :value="field.answer_points ?? ''"
                type="number"
                min="0"
                class="form-input w-20"
                @input="event => updateAnswerPoints(index, event.target.value)"
              />
            </div>
          </div>
        </div>
      </template>
    </Draggable>
  </div>
</template>
